import { useWhitelabel } from "hooks";
import usePayment, { type PaymentMethods } from "hooks/usePayment";
import { useMemo } from "react";
import { useMutation } from "react-query";
import {
  EnumOriginPayment,
  EnumTypeDebit,
  getInstallments,
  MappedDebitsToGetInstallments,
} from "services/CreditCard";
import { cleanMoneyMask } from "utils";
import { use3DSValidation } from "./use3DSValidation";

const DEFAULT_INSTALLMENT_NUMBER = 1;

interface useFlowResumeParams {
  goToResume?: () => void;
}

export function useFlowResume({ goToResume }: useFlowResumeParams) {
  const { dataPaymentBillet, paymentMethods, updateInstallments } =
    usePayment();
  const { client: clientWhitelabel } = useWhitelabel();
  const { initGet3DSValidation, isLoading } = use3DSValidation({ token: "", goToResume });

  const chargeMethods = useMemo(() => {
    return paymentMethods.map((method: PaymentMethods) => ({
      ...(method?.typeMethod === "credit" && {
        type: "CREDIT_CARD",
        installment_number: +method.installments,
        original_amount: cleanMoneyMask(method.value),
      }),
      ...(method?.typeMethod === "debit" && {
        type: "DEBIT_CARD",
        installment_number: DEFAULT_INSTALLMENT_NUMBER,
        original_amount: cleanMoneyMask(method.value),
      }),
      ...(method?.typeMethod === "pix" && {
        type: "PIX",
        installment_number: DEFAULT_INSTALLMENT_NUMBER,
        original_amount: cleanMoneyMask(method.value),
      }),
    }));
  }, [paymentMethods]);

  const { mutateAsync: mutateGetIntallments } = useMutation({
    mutationKey: ["get-installments"],
    mutationFn: async () => {
      const billets: MappedDebitsToGetInstallments =
        dataPaymentBillet.billets.map((billet) => ({
          type: EnumTypeDebit.BILLET,
          id_bill: billet.id_bill,
        }));
      const carDebits: MappedDebitsToGetInstallments =
        dataPaymentBillet.debits.filter(debit => !debit?.redirect_to_be_paid).map((debit) => ({
          type: EnumTypeDebit.CAR_DEBIT,
          id_debit: debit.id_debit,
        }));
      const pixDebits: MappedDebitsToGetInstallments =
        dataPaymentBillet.pix.map(({ id_pix }) => ({
          type: EnumTypeDebit.PIX,
          id_pix,
        }));
      const debits: MappedDebitsToGetInstallments = [
        ...billets,
        ...carDebits,
        ...pixDebits,
      ]

      const request = {
        idEstablishment: clientWhitelabel?.id_usuario,
        data: {
          origin: EnumOriginPayment.ONLINE,
          debits,
          ...(chargeMethods.length === 2 && { charge_methods: chargeMethods }),
        },
      };

      const installments = await getInstallments(request);

      return installments;
    },
    onSuccess(data) {
      updateInstallments(data);
      if (goToResume) {
        if (paymentMethods.length === 2) {
          initGet3DSValidation(data);
        } else {
          goToResume();
        }
      }
    },
  });

  return { getInstallments: mutateGetIntallments, isLoading };
}
